'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { CheckCircle2, Circle, AlertTriangle } from 'lucide-react'

interface StopBangQuestionnaireProps {
  responses: Record<string, boolean>
  onChange: (responses: Record<string, boolean>) => void
  biologicalSex: 'male' | 'female' | ''
  dateOfBirth: string
}

const questions = [
  { 
    id: 'snore', 
    text: 'Do you Snore loudly (louder than talking or loud enough to be heard through closed doors)?',
    letter: 'S'
  },
  { 
    id: 'tired', 
    text: 'Do you often feel Tired, fatigued, or sleepy during daytime?',
    letter: 'T'
  },
  { 
    id: 'observed_apneas', 
    text: 'Has anyone Observed you stop breathing during your sleep?',
    letter: 'O'
  },
  { 
    id: 'high_blood_pressure', 
    text: 'Do you have or are you being treated for high blood Pressure?',
    letter: 'P'
  },
]

export default function QuestionnaireStopBang({ 
  responses, 
  onChange, 
  biologicalSex,
  dateOfBirth 
}: StopBangQuestionnaireProps) {
  const handleAnswer = (questionId: string, value: boolean) => {
    onChange({
      ...responses,
      [questionId]: value
    })
  }

  // Auto-calculate demographic questions
  const calculateAge = () => {
    if (!dateOfBirth) return 0
    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const age = calculateAge()
  const ageOver50 = age > 50
  const isMale = biologicalSex === 'male'

  // BMI: auto-calculated from height + weight
  const [heightFeet, setHeightFeet] = useState('5')
  const [heightInches, setHeightInches] = useState('6')
  const [weightLbs, setWeightLbs] = useState('')

  const totalInches = (parseInt(heightFeet) || 0) * 12 + (parseInt(heightInches) || 0)
  const weightNum = parseFloat(weightLbs) || 0
  const calculatedBmi = totalInches > 0 && weightNum > 0
    ? Math.round((weightNum * 703) / (totalInches * totalInches) * 10) / 10
    : null

  // Auto-set bmi_over_35 whenever BMI is calculated
  useEffect(() => {
    if (calculatedBmi !== null) {
      const isOver35 = calculatedBmi >= 35
      if (responses.bmi_over_35 !== isOver35) {
        onChange({ ...responses, bmi_over_35: isOver35 })
      }
    }
  }, [calculatedBmi]) // eslint-disable-line react-hooks/exhaustive-deps

  const neckQuestion = {
    id: 'neck_over_40cm',
    text: 'Is your neck circumference greater than 40 cm (approximately 16 inches)?',
    letter: 'N',
    hint: 'Measure around the neck at the level of the Adam\'s apple'
  }

  // Calculate score
  const stopScore = questions.filter(q => responses[q.id] === true).length
  const bangScore = [
    responses.bmi_over_35,
    ageOver50,
    responses.neck_over_40cm,
    isMale
  ].filter(val => val === true).length

  const totalScore = stopScore + bangScore
  const allStopAnswered = questions.every(q => responses[q.id] !== undefined)
  const allBangAnswered = responses.bmi_over_35 !== undefined && responses.neck_over_40cm !== undefined

  const allAnswered = allStopAnswered && allBangAnswered

  // Determine risk category
  let riskCategory: 'low' | 'intermediate' | 'high' = 'low'
  if (totalScore >= 5) {
    riskCategory = 'high'
  } else if (totalScore >= 3) {
    riskCategory = 'intermediate'
  }

  // Additional high-risk criteria: STOP≥2 plus (male, BMI>35, or neck>40cm)
  if (stopScore >= 2 && (isMale || responses.bmi_over_35 || responses.neck_over_40cm)) {
    riskCategory = 'high'
  }

  return (
    <Card className="border-[#3B82F6]/30 bg-[#3B82F6]/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          STOP-Bang Questionnaire
          {allAnswered && (
            <Badge 
              variant={riskCategory === 'high' ? 'destructive' : 'default'}
              className={
                riskCategory === 'high' 
                  ? 'bg-red-500/20 text-red-400 border-red-500/30' 
                  : riskCategory === 'intermediate'
                  ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                  : 'bg-[#2FFFC7]/20 text-[#2FFFC7] border-[#2FFFC7]/30'
              }
            >
              Score: {totalScore} / 8 - {riskCategory.toUpperCase()}
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-white/60">
          Sleep Apnea Screening - Important for testosterone therapy safety
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* STOP Questions */}
        <div className="space-y-4">
          <div className="font-semibold text-sm bg-[#3B82F6]/20 text-[#3B82F6] px-3 py-2 rounded">
            STOP Questions
          </div>
          {questions.map((question) => (
            <div key={question.id} className="bg-white/5 border border-white/10 rounded-lg p-4">
              <div className="font-medium text-sm mb-3 text-white">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-[#3B82F6]/20 text-[#3B82F6] rounded-full text-xs font-bold mr-2">
                  {question.letter}
                </span>
                {question.text}
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handleAnswer(question.id, true)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 rounded-lg transition-all ${
                    responses[question.id] === true
                      ? 'border-[#3B82F6] bg-[#3B82F6]/10 text-[#3B82F6]'
                      : 'border-white/20 hover:border-[#3B82F6]/50 text-white/70'
                  }`}
                >
                  {responses[question.id] === true ? (
                    <CheckCircle2 className="h-5 w-5 text-[#3B82F6]" />
                  ) : (
                    <Circle className="h-5 w-5 text-white/40" />
                  )}
                  <span className="font-medium">Yes</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleAnswer(question.id, false)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 rounded-lg transition-all ${
                    responses[question.id] === false
                      ? 'border-white/50 bg-white/10 text-white'
                      : 'border-white/20 hover:border-white/40 text-white/70'
                  }`}
                >
                  {responses[question.id] === false ? (
                    <CheckCircle2 className="h-5 w-5 text-white" />
                  ) : (
                    <Circle className="h-5 w-5 text-white/40" />
                  )}
                  <span className="font-medium">No</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* BANG Questions */}
        <div className="space-y-4">
          <div className="font-semibold text-sm bg-[#3B82F6]/20 text-[#3B82F6] px-3 py-2 rounded">
            BANG Questions
          </div>

          {/* BMI — Auto-calculated from height & weight */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="font-medium text-sm mb-1 text-white">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-[#3B82F6]/20 text-[#3B82F6] rounded-full text-xs font-bold mr-2">
                B
              </span>
              BMI (Body Mass Index) more than 35 kg/m²?
            </div>
            <div className="text-xs text-white/50 mb-3 ml-8">
              Enter your height and weight — BMI is calculated automatically
            </div>

            {/* Height + Weight inputs */}
            <div className="ml-8 space-y-3 mb-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-white/50">Height</Label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Input
                      type="number"
                      value={heightFeet}
                      onChange={e => setHeightFeet(e.target.value)}
                      min={1}
                      max={8}
                      placeholder="5"
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-[#3B82F6] pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40">ft</span>
                  </div>
                  <div className="flex-1 relative">
                    <Input
                      type="number"
                      value={heightInches}
                      onChange={e => setHeightInches(e.target.value)}
                      min={0}
                      max={11}
                      placeholder="6"
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-[#3B82F6] pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40">in</span>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-white/50">Weight</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={weightLbs}
                    onChange={e => setWeightLbs(e.target.value)}
                    min={1}
                    placeholder="Enter weight"
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-[#3B82F6] pr-10"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40">lbs</span>
                </div>
              </div>
            </div>

            {/* Auto-calculated result */}
            <div className="ml-8">
              {calculatedBmi !== null ? (
                <>
                  <div className="flex items-center gap-3 mb-1">
                    <Badge
                      variant={responses.bmi_over_35 ? 'default' : 'outline'}
                      className={responses.bmi_over_35
                        ? 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30'
                        : 'bg-white/5 text-white/60 border-white/20'
                      }
                    >
                      {responses.bmi_over_35 ? 'Yes' : 'No'} — BMI {calculatedBmi}
                    </Badge>
                    <span className={`text-xs ${
                      calculatedBmi < 18.5 ? 'text-blue-400' :
                      calculatedBmi < 25 ? 'text-emerald-400' :
                      calculatedBmi < 30 ? 'text-amber-400' :
                      calculatedBmi < 35 ? 'text-orange-400' :
                      'text-red-400'
                    }`}>
                      {calculatedBmi < 18.5 ? 'Underweight' :
                       calculatedBmi < 25 ? 'Healthy' :
                       calculatedBmi < 30 ? 'Overweight' :
                       calculatedBmi < 35 ? 'Obesity I' :
                       calculatedBmi < 40 ? 'Obesity II' : 'Obesity III'}
                    </span>
                  </div>
                  <p className="text-xs text-white/50">
                    Auto-calculated from your height and weight
                  </p>
                </>
              ) : (
                <p className="text-xs text-white/40">
                  Enter your height and weight above to calculate BMI
                </p>
              )}
            </div>
          </div>

          {/* Auto-calculated: Age */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="font-medium text-sm mb-2 text-white">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-[#3B82F6]/20 text-[#3B82F6] rounded-full text-xs font-bold mr-2">
                A
              </span>
              Age over 50 years old?
            </div>
            <div className="ml-8">
              <Badge 
                variant={ageOver50 ? 'default' : 'outline'}
                className={ageOver50 
                  ? 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30'
                  : 'bg-white/5 text-white/60 border-white/20'
                }
              >
                {ageOver50 ? `Yes - Age ${age}` : `No - Age ${age}`}
              </Badge>
              <p className="text-xs text-white/50 mt-1">
                Auto-calculated from your date of birth
              </p>
            </div>
          </div>

          {/* Neck Circumference Question */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="font-medium text-sm mb-1 text-white">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-[#3B82F6]/20 text-[#3B82F6] rounded-full text-xs font-bold mr-2">
                N
              </span>
              {neckQuestion.text}
            </div>
            <div className="text-xs text-white/50 mb-3 ml-8">
              {neckQuestion.hint}
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => handleAnswer('neck_over_40cm', true)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 rounded-lg transition-all ${
                  responses.neck_over_40cm === true
                    ? 'border-[#3B82F6] bg-[#3B82F6]/10 text-[#3B82F6]'
                    : 'border-white/20 hover:border-[#3B82F6]/50 text-white/70'
                }`}
              >
                {responses.neck_over_40cm === true ? (
                  <CheckCircle2 className="h-5 w-5 text-[#3B82F6]" />
                ) : (
                  <Circle className="h-5 w-5 text-white/40" />
                )}
                <span className="font-medium">Yes</span>
              </button>
              <button
                type="button"
                onClick={() => handleAnswer('neck_over_40cm', false)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 rounded-lg transition-all ${
                  responses.neck_over_40cm === false
                    ? 'border-white/50 bg-white/10 text-white'
                    : 'border-white/20 hover:border-white/40 text-white/70'
                }`}
              >
                {responses.neck_over_40cm === false ? (
                  <CheckCircle2 className="h-5 w-5 text-white" />
                ) : (
                  <Circle className="h-5 w-5 text-white/40" />
                )}
                <span className="font-medium">No</span>
              </button>
            </div>
          </div>

          {/* Auto-calculated: Gender */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="font-medium text-sm mb-2 text-white">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-[#3B82F6]/20 text-[#3B82F6] rounded-full text-xs font-bold mr-2">
                G
              </span>
              Gender: Male?
            </div>
            <div className="ml-8">
              <Badge 
                variant={isMale ? 'default' : 'outline'}
                className={isMale 
                  ? 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30'
                  : 'bg-white/5 text-white/60 border-white/20'
                }
              >
                {isMale ? 'Yes - Male' : 'No - Female'}
              </Badge>
              <p className="text-xs text-white/50 mt-1">
                Based on your biological sex selection
              </p>
            </div>
          </div>
        </div>

        {/* Score Interpretation */}
        {allAnswered && (
          <div className={`mt-6 p-4 rounded-lg border-2 ${
            riskCategory === 'high' 
              ? 'bg-red-500/10 border-red-500/30' 
              : riskCategory === 'intermediate'
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-[#2FFFC7]/10 border-[#2FFFC7]/30'
          }`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                {riskCategory === 'high' ? (
                  <AlertTriangle className="h-8 w-8 text-red-400" />
                ) : riskCategory === 'intermediate' ? (
                  <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold">
                    !
                  </div>
                ) : (
                  <CheckCircle2 className="h-8 w-8 text-[#2FFFC7]" />
                )}
              </div>
              <div className="flex-1">
                <div className={`font-semibold mb-1 ${
                  riskCategory === 'high' ? 'text-red-400' : 
                  riskCategory === 'intermediate' ? 'text-amber-400' : 
                  'text-[#2FFFC7]'
                }`}>
                  {riskCategory === 'high' && 'High Risk for Sleep Apnea'}
                  {riskCategory === 'intermediate' && 'Intermediate Risk for Sleep Apnea'}
                  {riskCategory === 'low' && 'Low Risk for Sleep Apnea'}
                </div>
                <div className={`text-sm mb-2 ${
                  riskCategory === 'high' ? 'text-red-300/80' : 
                  riskCategory === 'intermediate' ? 'text-amber-300/80' : 
                  'text-[#2FFFC7]/80'
                }`}>
                  {riskCategory === 'high' && (
                    <>
                      <strong>Sleep study may be recommended before testosterone therapy.</strong> Untreated sleep apnea can worsen with TRT. Your provider will review this with you.
                    </>
                  )}
                  {riskCategory === 'intermediate' && (
                    <>
                      Moderate risk of obstructive sleep apnea. Your provider may recommend sleep testing based on your full clinical picture.
                    </>
                  )}
                  {riskCategory === 'low' && (
                    <>
                      Low probability of obstructive sleep apnea. Your provider will still review your symptoms holistically.
                    </>
                  )}
                </div>
                <div className="text-xs text-white/50">
                  <strong>Score breakdown:</strong> STOP: {stopScore} / BANG: {bangScore} = Total: {totalScore} / 8
                </div>
              </div>
            </div>
          </div>
        )}

        {!allAnswered && (
          <div className="text-sm text-white/50 text-center py-2">
            Please answer all questions to see your score
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export { questions as stopBangQuestions }
